import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { LoginProof, LoginPath } from '../types/identity';

const QR_SIZE = 200;

function buildLoginSignUrl(hash: string, nonce: string): string {
  const params = new URLSearchParams({ hash, nonce });
  return `${window.location.origin}/login-sign?${params.toString()}`;
}

function ProgressLine(p: { satisfied: number; required: number }): JSX.Element {
  return (
    <p role="status" aria-live="polite">
      Signatures collectées : {p.satisfied} / {p.required}
    </p>
  );
}

function MaybeProgress(p: {
  collectProgress: { satisfied: number; required: number } | null | undefined;
}): JSX.Element | null {
  const c = p.collectProgress;
  if (c === undefined || c === null || c.required <= 0) {
    return null;
  }
  return <ProgressLine satisfied={c.satisfied} required={c.required} />;
}

interface LoginCollectShareProps {
  proof: LoginProof;
  loginPath: LoginPath | null;
  /** Progress from relay fetch (notifications). X/Y signatures. */
  collectProgress?: { satisfied: number; required: number } | null;
  /** Collected signatures from remote devices. */
  collectedSignatures?: Array<{
    signature: string;
    cle_publique: string;
    nonce: string;
    pair_uuid: string;
  }>;
  /** Callback to refresh signatures from relays. */
  onRefresh?: () => void;
  /** Callback to view signature details. */
  onViewDetails?: (requirement: string, pairUuid: string | undefined) => void;
}

/**
 * Device 1: show link + QR for "Demander signature sur l'autre appareil" during collect.
 * Displays progress (X/Y) when collectProgress provided.
 * Shows detailed signature status when loginPath and collectedSignatures provided.
 */
export function LoginCollectShare({
  proof,
  loginPath,
  collectProgress,
  collectedSignatures,
  onRefresh,
  onViewDetails,
}: LoginCollectShareProps): JSX.Element {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const url = buildLoginSignUrl(proof.challenge.hash, proof.challenge.nonce);

  useEffect(() => {
    QRCode.toDataURL(url, { width: QR_SIZE })
      .then(setQrDataUrl)
      .catch((err: unknown) => {
        console.error('QR login-sign failed:', err);
      });
  }, [url]);

  const getSignatureStatus = (): Array<{
    requirement: string;
    pairUuid: string | undefined;
    clePublique: string | undefined;
    status: 'manquante' | 'reçue' | 'valide' | 'invalide';
    signature?: string;
  }> => {
    if (loginPath === null) {
      return [];
    }

    const allSignatures = [
      ...proof.signatures,
      ...(collectedSignatures ?? []),
    ];

    return loginPath.signatures_requises.map((req) => {
      // Trouver la signature correspondante
      const matchingSig = allSignatures.find(
        (s) => s.cle_publique === req.cle_publique,
      );

      let status: 'manquante' | 'reçue' | 'valide' | 'invalide';
      if (matchingSig !== undefined) {
        // Signature trouvée - considérée comme valide si elle correspond à la clé requise
        // Note: la vérification crypto complète est faite ailleurs, ici on indique juste "reçue"
        status = 'reçue';
      } else {
        status = 'manquante';
      }

      return {
        requirement: `${req.membre_uuid.slice(0, 8)}...`,
        pairUuid: req.pair_uuid,
        clePublique: req.cle_publique,
        status,
        signature: matchingSig?.signature,
      };
    });
  };

  const signatureStatuses = getSignatureStatus();

  return (
    <section aria-label="Collecte signatures mFA">
      <h3>Collecte signatures mFA</h3>
      <MaybeProgress collectProgress={collectProgress} />
      {signatureStatuses.length > 0 && (
        <div>
          <h4>Signatures requises</h4>
          {onRefresh !== undefined && (
            <button type="button" onClick={onRefresh}>
              Rafraîchir
            </button>
          )}
          <table>
            <thead>
              <tr>
                <th>Membre</th>
                <th>Pair</th>
                <th>Clé publique</th>
                <th>État</th>
                {onViewDetails !== undefined && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {signatureStatuses.map((sig, idx) => (
                <tr key={idx}>
                  <td>{sig.requirement}</td>
                  <td>
                    {sig.pairUuid !== undefined
                      ? `${sig.pairUuid.slice(0, 8)}...`
                      : '—'}
                  </td>
                  <td>
                    {sig.clePublique !== undefined
                      ? `${sig.clePublique.slice(0, 16)}...`
                      : '—'}
                  </td>
                  <td>{sig.status}</td>
                  {onViewDetails !== undefined && (
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          onViewDetails(sig.requirement, sig.pairUuid);
                        }}
                      >
                        Voir détail
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div>
        <h4>{`Demander signature sur l${"'"}autre appareil`}</h4>
        <p>Ouvrez ce lien ou scannez le QR sur le 2ᵉ appareil.</p>
        <p>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </p>
        {qrDataUrl !== null && (
          <img
            src={qrDataUrl}
            alt="QR code : lien pour signer le login sur le 2e appareil"
            width={QR_SIZE}
            height={QR_SIZE}
          />
        )}
      </div>
    </section>
  );
}
